import React, { useState } from "react";
import styles from "./controls.module.css";

const OPTIONS = {
  filename: "last-human-demo",
  fileType: "mp4",
  width: 640,
  height: 480,
};

const recordDuration = 7;
const secondsBeforeRecord = 3;
const resetMins = 5;
const outputCanvasWidth = 654;
const allowGhosting = false;
const useRealColours = false;
const blocksWide = 128;

const smallCanvasWtoHRatio = OPTIONS.height / OPTIONS.width;
const outputCanvasHeight = outputCanvasWidth * smallCanvasWtoHRatio;
const outputCanvasSize = { w: outputCanvasWidth, h: outputCanvasHeight };
const blockSize = Math.round(outputCanvasWidth / blocksWide);
const smallCanvasSize = {
  w: blocksWide,
  h: Math.round(smallCanvasWtoHRatio * blocksWide),
};

export const defaultSettings = {
  recordDuration,
  secondsBeforeRecord,
  resetMins,
  outputCanvasWidth,
  allowGhosting,
  useRealColours,
  blocksWide,
  smallCanvasSize,
  blockSize,
  outputCanvasSize,
  blockColour: "#ffa200",
  ghostingLevel: 0.05,
  vertOffset: 128,
  showWebcamPreview: true,
  maxBlockFraction: 1,
  blockShape: "square",
  cycleColours: false,
  fadeEdges: true,
  showCubeFront: false,
  showCubeBack: false,
  showCube: false,
  OPTIONS,
};

export default function Controls({ settings, setSettings }) {
  const [showJson, setShowJson] = useState(false);

  const updateSettings = (key, value) => {
    const { blocksWide, outputCanvasWidth } = settings;

    const outputCanvasHeight = outputCanvasWidth * smallCanvasWtoHRatio;
    const outputCanvasSize = { w: outputCanvasWidth, h: outputCanvasHeight };
    const blockSize = Math.round(outputCanvasWidth / blocksWide);
    const smallCanvasSize = {
      w: blocksWide,
      h: Math.round(smallCanvasWtoHRatio * blocksWide),
    };

    setSettings({
      ...settings,
      blockSize,
      outputCanvasSize,
      smallCanvasSize,
      [key]: value,
    });
  };

  const json = JSON.stringify(settings, undefined, 2);

  const onReset = () => setSettings({ ...defaultSettings });
  const onReload = () => window.location.reload();

  return (
    <div className={styles.controls}>
      <button onClick={onReload}>RELOAD :)</button>
      <button onClick={onReset}>RESET :)</button>

      <Ctrl
        type="range"
        prop="blocksWide"
        min="24"
        max="300"
        step="2"
        value={settings.blocksWide}
        updateSettings={updateSettings}
      />

      <Ctrl
        type="select"
        prop="blockShape"
        options={["square", "circle"]}
        value={settings.blockShape}
        updateSettings={updateSettings}
      />

      <Ctrl
        type="range"
        prop="outputCanvasWidth"
        min="400"
        max="1000"
        step="2"
        value={settings.outputCanvasWidth}
        updateSettings={updateSettings}
      />

      <Ctrl
        type="range"
        prop="maxBlockFraction"
        min="0.1"
        max="3"
        step="0.01"
        value={settings.maxBlockFraction}
        updateSettings={updateSettings}
      />

      <Ctrl
        type="range"
        prop="vertOffset"
        min="0"
        max="600"
        step="2"
        value={settings.vertOffset}
        updateSettings={updateSettings}
      />

      <Ctrl
        type="checkbox"
        prop="showCubeFront"
        value={settings.showCubeFront}
        updateSettings={updateSettings}
      />

      <Ctrl
        type="checkbox"
        prop="showCubeBack"
        value={settings.showCubeBack}
        updateSettings={updateSettings}
      />

      <Ctrl
        type="checkbox"
        prop="fadeEdges"
        value={settings.fadeEdges}
        updateSettings={updateSettings}
      />

      <Ctrl
        type="number"
        prop="recordDuration"
        value={settings.recordDuration}
        updateSettings={updateSettings}
      />

      <Ctrl
        type="number"
        prop="secondsBeforeRecord"
        value={settings.secondsBeforeRecord}
        updateSettings={updateSettings}
      />

      <Ctrl
        type="checkbox"
        prop="cycleColours"
        value={settings.cycleColours}
        updateSettings={updateSettings}
      />

      {!settings.cycleColours && (
        <Ctrl
          type="checkbox"
          prop="useRealColours"
          value={settings.useRealColours}
          updateSettings={updateSettings}
        />
      )}

      {!settings.useRealColours && !settings.cycleColours && (
        <Ctrl
          type="color"
          prop="blockColour"
          value={settings.blockColour}
          updateSettings={updateSettings}
        />
      )}

      <Ctrl
        type="checkbox"
        prop="showWebcamPreview"
        value={settings.showWebcamPreview}
        updateSettings={updateSettings}
      />

      <Ctrl
        type="checkbox"
        prop="allowGhosting"
        value={settings.allowGhosting}
        updateSettings={updateSettings}
      />

      {settings.allowGhosting && (
        <Ctrl
          type="range"
          prop="ghostingLevel"
          min="0"
          max="0.3"
          step="0.001"
          value={settings.ghostingLevel}
          updateSettings={updateSettings}
        />
      )}

      <button onClick={() => setShowJson(!showJson)}>Toggle JSON</button>

      {showJson && <pre>{json}</pre>}
    </div>
  );
}

const Ctrl = ({
  prop,
  value,
  type,
  updateSettings,
  min = "0",
  max = "300",
  step = "1",
  options = [],
}) => {
  let input = null;

  if (type === "checkbox") {
    input = (
      <input
        type={type}
        checked={value}
        onChange={(e) => updateSettings(prop, e.target.checked)}
      />
    );
  } else if (type === "select") {
    input = (
      <select
        value={value}
        onChange={(e) => updateSettings(prop, e.target.value)}
      >
        {options.map((option, i) => (
          <option key={i} value={option}>
            {option}
          </option>
        ))}
      </select>
    );
  } else if (type === "range") {
    input = (
      <input
        type={type}
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(e) => updateSettings(prop, e.target.value)}
      />
    );
  } else {
    input = (
      <input
        type={type}
        value={value}
        onChange={(e) => updateSettings(prop, e.target.value)}
      />
    );
  }

  return (
    <div className={styles.ctrl}>
      <label>
        {prop}: {input}
      </label>
    </div>
  );
};
